<template>
  <generic-modal
    :title="$t('order detail')"
    ref="generic_modal"
    :is_wide="true"
  >
    <!-- Loading State -->
    <!-- <div class="has-text-centered py-6" v-if="!order_loaded && !error">
      <div class="spinner-border" role="status">
        <span class="icon is-large has-text-primary">
          <i class="fa fa-spinner fa-pulse fa-2x"></i>
        </span>
      </div>
      <p class="mt-3">{{ $t("loading") }}...</p>
    </div> -->

    <!-- Order Content -->
    <div v-if="order_loaded" class="order-detail-content">
      <!-- Header Card with Order Summary -->
      <div class="card mb-0">
        <div class="card-content pt-0">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <div>
                  <p class="title is-5">
                    <span class="icon has-text-primary">
                      <i class="fa fa-shopping-cart"></i>
                    </span>
                    {{ $t("order") }} #{{ order.uuid?.substring(0, 8) }}
                  </p>
                  <p class="subtitle is-6 has-text-grey">
                    {{ format_date_time(order.create_date) }}
                  </p>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <div class="tags has-addons">
                  <span class="tag is-dark">{{ $t("status") }}</span>
                  <span class="tag" :class="getStatusClass(order.status)">
                    {{ $t(order.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="columns is-multiline">
        <!-- Customer Information -->
        <div class="column is-6">
          <div class="card h-100">
            <div class="card-header">
              <div class="card-header-title">
                <span class="icon has-text-primary mr-2">
                  <i class="fa fa-user"></i>
                </span>
                {{ $t("customer information") }}
              </div>
            </div>
            <div class="card-content">
              <div class="media">
                <div class="media-left">
                  <figure class="image is-48x48">
                    <div class="avatar-placeholder">
                      <span class="icon is-large has-text-primary">
                        <i class="fa fa-user fa-2x"></i>
                      </span>
                    </div>
                  </figure>
                </div>
                <div class="media-content">
                  <p class="title is-6 mb-0">{{ order.customer.name }}</p>
                  <div class="content">
                    <div class="field-group" v-if="order.customer.email">
                      <span class="icon is-small has-text-info">
                        <i class="fa fa-envelope"></i>
                      </span>
                      <span>{{ order.customer.email }}</span>
                    </div>
                    <div class="field-group" v-if="order.customer.phone">
                      <span class="icon is-small has-text-success">
                        <i class="fa fa-phone"></i>
                      </span>
                      <span>{{ order.customer.phone }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Details Card -->
          <div class="card mt-4">
            <div class="card-header">
              <div class="card-header-title">
                <span class="icon has-text-info mr-2">
                  <i class="fa fa-info-circle"></i>
                </span>
                {{ $t("order details") }}
              </div>
            </div>
            <div class="card-content">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">
                    <span class="icon is-small">
                      <i class="fa fa-credit-card"></i>
                    </span>
                    {{ $t("payment method") }}
                  </span>
                  <span class="detail-value">
                    {{ $t(order.payment_method || "not set") }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    <span class="icon is-small">
                      <i class="fa fa-money"></i>
                    </span>
                    {{ $t("payment status") }}
                  </span>
                  <span class="detail-value">
                    <span
                      class="tag is-small"
                      :class="getPaymentStatusClass(order.payment_status)"
                    >
                      {{ $t(order.payment_status || "unpaid") }}
                    </span>
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    <span class="icon is-small">
                      <i class="fa fa-globe"></i>
                    </span>
                    {{ $t("source") }}
                  </span>
                  <span class="detail-value">{{ order.source }}</span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    <span class="icon is-small">
                      <i class="fa fa-calendar"></i>
                    </span>
                    {{ $t("order date") }}
                  </span>
                  <span class="detail-value">
                    {{ format_date_time(order.create_date) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Products List -->
        <div class="column is-6">
          <div class="card h-100">
            <div class="card-header">
              <div class="card-header-title">
                <span class="icon has-text-success mr-2">
                  <i class="fa fa-shopping-bag"></i>
                </span>
                {{ $t("products") }}
                <span class="tag is-dark ml-2">
                  {{ order.order_order_items?.length || 0 }} {{ $t("items") }}
                </span>
              </div>
            </div>
            <div class="card-content p-0">
              <div class="products-list">
                <div
                  v-for="(item, index) in order.order_order_items"
                  :key="index"
                  class="product-item"
                >
                  <div class="columns is-mobile is-vcentered">
                    <div class="column">
                      <div class="product-info">
                        <p class="product-name">{{ item.product.name }}</p>
                        <p class="product-details">
                          {{ item.quantity }} ×
                          {{ format_currency(item.price) }}
                        </p>
                      </div>
                    </div>
                    <div class="column is-narrow">
                      <span class="product-total">
                        {{
                          format_currency(
                            item.quantity * parseFloat(item.price)
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Total Section -->
              <div class="card-footer">
                <div class="card-footer-item">
                  <div class="level is-fullwidth">
                    <div class="level-left">
                      <div class="level-item">
                        <strong>{{ $t("total") }}</strong>
                      </div>
                    </div>
                    <div class="level-right">
                      <div class="level-item">
                        <span class="total-amount">
                          {{ format_currency(order.total) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div class="notification is-danger" v-if="error">
      <span class="icon">
        <i class="fa fa-exclamation-triangle"></i>
      </span>
      <span v-html="error"></span>
    </div>

    <!-- Footer -->
    <template v-slot:footer>
      <button class="button" @click="close_modal">
        <span class="icon">
          <i class="fa fa-times"></i>
        </span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>
<script>
import {
  put,
  error_message,
  get,
  post,
  error_alert,
  format_date_time,
  api_host,
  format_currency,
  generate_error_message_text,
  get_currency_settings,
  change_active_tab,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "OrderModal",
  components: {
    GenericModal,
    GenericDropdown,
  },
  props: {},
  data() {
    return {
      order_loaded: false,
      api_host,
      order: {},
      error: "",
    };
  },
  computed: {
    // is_form_invalid() {
    //   let v = this;
    //   // return false;
    //   return !v.order.name /* || !v.order.sale_price */;
    // },
  },
  methods: {
    format_currency,
    format_date_time,
    getStatusClass(status) {
      const statusClasses = {
        pending: "is-warning",
        confirmed: "is-info",
        processing: "is-primary",
        shipped: "is-link",
        delivered: "is-success",
        cancelled: "is-danger",
      };
      return statusClasses[status] || "is-dark";
    },
    getPaymentStatusClass(status) {
      const paymentClasses = {
        paid: "is-success",
        pending: "is-warning",
        failed: "is-danger",
        unpaid: "is-dark",
        refunded: "is-info",
      };
      return paymentClasses[status] || "is-dark";
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    show(order_uuid) {
      let v = this;
      v.set_initial_state();
      v.$root.loader(true);
      get("/order/order", { uuid: order_uuid })
        .then((order) => {
          v.order = order;
          v.order_loaded = true;
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });

      v.$refs.generic_modal.show_modal();
    },
    // save() {
    //   let v = this;
    //   let data = { ...v.order };
    //   data.settings.attributes = v.$refs.attributes.get_attributes();
    //   if (!v.order.uuid)
    //     put("/product", data)
    //       .then(() => {
    //         v.$emit("success");
    //         v.$refs.generic_modal.close_modal();
    //       })
    //       .catch((error) => {
    //         v.error = generate_error_message_text(error);
    //       });
    //   else
    //     post("/product", data)
    //       .then(() => {
    //         v.$emit("success");
    //         v.$refs.generic_modal.close_modal();
    //       })
    //       .catch((error) => {
    //         v.error = generate_error_message_text(error);
    //       });
    // },
    set_initial_state() {
      let v = this;
      v.order = {
        settings: {},
      };
      v.error = "";
      v.order_loaded = false;
      // v.uuid = "";
    },
    // error_uploading(error) {
    //   let v = this;
    //   v.error = generate_error_message_text(error);
    // },
    // success_uploading(response) {
    //   let v = this;
    //   let rspnse = JSON.parse(response.serverId);
    //   let data = {
    //     file_id: rspnse.file.file_id,
    //     final_name: rspnse.final_name,
    //     order: rspnse.file.settings,
    //     original_name: rspnse.file.name,
    //   };
    //   if (v.order.settings.images) {
    //     v.order.settings.images.push(data);
    //   } else {
    //     v.order.settings.images = [data];
    //   }
    // },
    // set_category(categories) {
    //   let v = this;
    //   v.order.categories = categories;
    // },
  },
  mounted() {},
  watch: {
    // is_form_invalid() {
    //   let v = this;
    //   v.buttons[0].disabled = v.is_form_invalid;
    // },
  },
};
</script>
<style scoped>
/* Order Detail Content */
.order-detail-content {
  padding: 0;
}

/* Cards */
.card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* .h-100 { */
/* height: 100%; */
/* } */

/* Avatar Placeholder */
.avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Field Groups */
.field-group {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.field-group .icon {
  margin-right: 0.5rem;
}

/* Detail Grid */
.detail-grid {
  display: grid;
  gap: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #fafafa;
  border-radius: 6px;
  border-left: 3px solid #dbdbdb;
  transition: border-color 0.3s ease;
}

.detail-item:hover {
  border-left-color: #3273dc;
}

.detail-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #4a4a4a;
}

.detail-label .icon {
  margin-right: 0.5rem;
}

.detail-value {
  font-weight: 500;
  color: #363636;
}

/* Products List */
.products-list {
  max-height: 400px;
  overflow-y: auto;
}

.product-item {
  padding: 1rem;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.3s ease;
}

.product-item:hover {
  background-color: rgb(68, 68, 68);
}

.product-item:last-child {
  border-bottom: none;
}

.product-info {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #d2d2d2;
  margin-bottom: 0.25rem;
}

.product-details {
  font-size: 0.875rem;
  color: #aeaeae;
  margin: 0;
}

.product-total {
  font-weight: 700;
  color: #23d160;
  font-size: 1.1rem;
}

/* Total Amount */
.total-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: #23d160;
}

/* Status and Payment Status Tags */
.tag.is-warning {
  background-color: #ffdd57;
  color: rgba(0, 0, 0, 0.7);
}

.tag.is-info {
  background-color: #3273dc;
  color: white;
}

.tag.is-success {
  background-color: #23d160;
  color: white;
}

.tag.is-danger {
  background-color: #ff3860;
  color: white;
}

.tag.is-primary {
  background-color: #00d1b2;
  color: white;
}

.tag.is-link {
  background-color: #3273dc;
  color: white;
}

/* Loading State */
.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .product-item .columns {
    margin: 0;
  }
}
</style>
